import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Input } from "../ui/input";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Loader2, Building2 } from "lucide-react";

/**
 * MoveAttorneyDialog — reparents an attorney to a different firm.
 * Type-ahead search hits GET /operations/firms/search?q=…
 * Confirm hits POST /operations/attorneys/reparent (new endpoint in funding_service.py).
 *
 * Note: loans keep their firm_id. Use the existing "Reassign loans" tool
 * (firms.py:401 POST /loans/reassign) separately if you also want to move the loans.
 */
export function MoveAttorneyDialog({ open, onOpenChange, attorneyEmail, currentFirmId, onMoved }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) { setQ(""); setResults([]); setSelected(null); return; }
  }, [open]);

  useEffect(() => {
    if (!q.trim()) { setResults([]); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/operations/firms/search?q=${encodeURIComponent(q)}`);
        if (!cancelled) setResults((data.firms || data || []).filter((f) => f.firm_id !== currentFirmId).slice(0, 8));
      } catch { /* ignore */ }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [q, currentFirmId]);

  const submit = async () => {
    if (!selected) return toast.error("Pick a destination firm");
    setSubmitting(true);
    try {
      const { data } = await api.post("/operations/attorneys/reparent", {
        attorney_email: attorneyEmail,
        new_firm_id: selected.firm_id,
      });
      toast.success(`Attorney moved to ${selected.firm_name}`);
      onMoved?.(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px]" data-testid="move-attorney-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl text-[#0A2540]">Move to another firm</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label className="text-xs text-[#374151] mb-1.5 block">Attorney</Label>
            <div className="text-sm text-[#0A2540]">{attorneyEmail}</div>
          </div>
          <div>
            <Label className="text-xs text-[#374151] mb-1.5 block">Destination firm</Label>
            <Input value={q} onChange={(e) => { setQ(e.target.value); setSelected(null); }} placeholder="Type to search firms…" data-testid="move-firm-search" />
            {results.length > 0 && !selected ? (
              <ul className="mt-2 border border-[#E5E7EB] rounded-md divide-y divide-[#F3F4F6] max-h-56 overflow-y-auto bg-white">
                {results.map((f) => (
                  <li key={f.firm_id}>
                    <button
                      type="button"
                      onClick={() => setSelected(f)}
                      className="w-full text-left px-3 py-2 hover:bg-[#F9FAFB] flex items-center gap-2"
                      data-testid={`move-firm-option-${f.firm_id}`}
                    >
                      <Building2 className="w-4 h-4 text-[#0066FF]" />
                      <span className="text-sm text-[#0A2540]">{f.firm_name}</span>
                      {f.domains?.length ? <span className="text-xs text-[#6B7280]">· {f.domains[0]}</span> : null}
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
            {selected ? (
              <div className="mt-2 flex items-center gap-2 rounded-md bg-[#F0F7FF] border border-[#BFDBFE] px-3 py-2 text-sm text-[#0A2540]">
                <Building2 className="w-4 h-4 text-[#0066FF]" /> {selected.firm_name}
                <button
                  type="button"
                  onClick={() => setSelected(null)}
                  className="ml-auto text-xs text-[#0066FF] hover:text-[#0052CC]"
                >
                  change
                </button>
              </div>
            ) : null}
          </div>
          <div className="text-xs text-[#6B7280] rounded-md bg-[#F9FAFB] border border-[#E5E7EB] p-3">
            Existing loans keep their firm assignment. Use <em>Firms → Reassign loans</em> if you also want to move loans.
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={submitting || !selected} className="bg-[#0066FF] hover:bg-[#0052CC]" data-testid="move-attorney-submit">
            {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Moving…</> : "Move attorney"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
